import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { api } from "../api/client";
import { useAuth, type ViewAsLevel } from "../context/AuthContext";
import type { AccessLevel } from "../types";
import { Eye, Lock, LockOpen } from "lucide-react";

/** The preview every admin gets with one click: what a standard employee
 *  with no extra permissions sees on their own pages. */
export function standardEmployeeView(label: string): ViewAsLevel {
  return {
    name: label,
    is_admin: false,
    can_view_compensation: false,
    can_edit_compensation: false,
    can_view_documents: false,
    can_manage_training: false,
    can_manage_leave: false,
    can_manage_onboarding: false,
    can_view_reports: false,
    can_manage_payroll: false,
    can_manage_certifications: false,
    can_manage_settings: false,
    can_manage_announcements: false,
    can_manage_expenses: false,
    can_manage_performance: false,
    can_manage_employees: false,
    can_approve_leave: false,
    can_approve_expenses: false,
    can_start_reviews: false,
    can_view_team: false,
    can_manage_roles: false,
    can_view_bank_details: false,
    can_edit_bank_details: false,
    can_view_bonuses: false,
    can_edit_bonuses: false,
    can_manage_deductions: false,
  };
}

const ADMIN_UNLOCK_KEY = "adminUnlocked";
const ADMIN_UNLOCK_TS_KEY = "adminUnlockedAt";
const ADMIN_UNLOCK_TIMEOUT_MS = 30 * 60 * 1000;

export function useAdminUnlock() {
  const { isSuperAdmin } = useAuth();

  const [unlocked, setUnlockedRaw] = useState(() => {
    if (typeof window === "undefined") return false;
    const val = sessionStorage.getItem(ADMIN_UNLOCK_KEY);
    if (val !== "true") return false;
    const ts = sessionStorage.getItem(ADMIN_UNLOCK_TS_KEY);
    if (!ts) return false;
    return Date.now() - Number(ts) < ADMIN_UNLOCK_TIMEOUT_MS;
  });

  const setUnlocked = useCallback((v: boolean) => {
    setUnlockedRaw(v);
    if (v) {
      sessionStorage.setItem(ADMIN_UNLOCK_KEY, "true");
      sessionStorage.setItem(ADMIN_UNLOCK_TS_KEY, String(Date.now()));
    } else {
      sessionStorage.removeItem(ADMIN_UNLOCK_KEY);
      sessionStorage.removeItem(ADMIN_UNLOCK_TS_KEY);
    }
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    const check = setInterval(() => {
      const ts = sessionStorage.getItem(ADMIN_UNLOCK_TS_KEY);
      if (!ts || Date.now() - Number(ts) >= ADMIN_UNLOCK_TIMEOUT_MS) {
        setUnlocked(false);
      }
    }, 60_000);
    return () => clearInterval(check);
  }, [unlocked, setUnlocked]);

  return { adminUnlocked: isSuperAdmin && unlocked, setAdminUnlocked: setUnlocked };
}

// ---------------------------------------------------------------------------
// Shared: Admin PIN Modal
// ---------------------------------------------------------------------------

export function AdminPinModal({ onSuccess, onClose }: { onSuccess: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [pinValue, setPinValue] = useState("");
  const [pinError, setPinError] = useState("");
  const [pinLoading, setPinLoading] = useState(false);

  const handleSubmit = async () => {
    if (!pinValue.trim()) return;
    setPinLoading(true);
    setPinError("");
    try {
      await api.post("/api/auth/verify-admin-pin", { pin: pinValue });
      onSuccess();
    } catch {
      setPinError(t("adminUnlock.invalidPin", "Invalid PIN"));
    }
    setPinLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-full max-w-xs rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-gray-900 mb-1">{t("adminUnlock.unlock", "Unlock admin view")}</h3>
        <p className="text-xs text-gray-500 mb-4">{t("adminUnlock.enterPin", "Enter your admin PIN to unlock full data access")}</p>
        <input
          type="password"
          inputMode="numeric"
          maxLength={6}
          value={pinValue}
          onChange={(e) => { setPinValue(e.target.value.replace(/\D/g, "")); setPinError(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") void handleSubmit(); }}
          placeholder="••••••"
          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-center text-lg tracking-widest focus:border-pme-blue focus:ring-1 focus:ring-pme-blue"
          autoFocus
        />
        {pinError && <p className="mt-1.5 text-xs text-red-600">{pinError}</p>}
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={pinLoading || pinValue.length < 4}
            className="flex-1 rounded-lg bg-pme-blue px-3 py-2 text-sm font-semibold text-white hover:bg-pme-blue/90 disabled:opacity-50"
          >
            {pinLoading ? "..." : t("adminUnlock.unlockBtn", "Unlock")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared: View As Dropdown Panel
// ---------------------------------------------------------------------------

export function ViewAsDropdownPanel({
  onClose,
  initialMode,
}: {
  onClose: () => void;
  initialMode?: "employee" | "level";
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isSuperAdmin, viewAsLevel, setViewAsLevel } = useAuth();

  const [accessLevels, setAccessLevels] = useState<AccessLevel[]>([]);
  const [viewAsMode, setViewAsMode] = useState<"employee" | "level" | null>(initialMode ?? null);
  const [empSearch, setEmpSearch] = useState("");
  const [empResults, setEmpResults] = useState<Array<{ id: number; first_name: string; last_name: string; site_name?: string; access_level_name?: string; rank?: number }>>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectError, setSelectError] = useState<string | null>(null);

  useEffect(() => {
    if (isSuperAdmin) {
      void (async () => { try { setAccessLevels(await api.get<AccessLevel[]>("/api/access-levels")); } catch {} })();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (viewAsMode !== "employee" || empSearch.length < 2) {
      setEmpResults([]);
      return;
    }
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get<Array<{ id: number; first_name: string; last_name: string; site_name?: string; access_level_name?: string; rank?: number }>>(`/api/employees?search=${encodeURIComponent(empSearch)}&is_active=true`);
        setEmpResults(res);
      } catch { setEmpResults([]); }
      finally { setSearchLoading(false); }
    }, 200);
    return () => clearTimeout(timer);
  }, [empSearch, viewAsMode]);

  const selectEmployee = useCallback(async (emp: { id: number; first_name: string; last_name: string; access_level_name?: string; rank?: number }) => {
    try {
      const full = await api.get<Record<string, unknown>>(`/api/employees/${emp.id}`);
      const level: ViewAsLevel = {
        name: `${emp.first_name} ${emp.last_name}`,
        employee_id: emp.id,
        is_admin: !!full.is_admin,
        rank: typeof full.rank === "number" ? full.rank : (emp.rank ?? undefined),
        access_level_name: typeof full.access_level_name === "string" ? full.access_level_name : (emp.access_level_name ?? undefined),
        can_view_compensation: !!full.can_view_compensation,
        can_edit_compensation: !!full.can_edit_compensation,
        can_view_documents: !!full.can_view_documents,
        can_manage_training: !!full.can_manage_training,
        can_manage_leave: !!full.can_manage_leave,
        can_manage_onboarding: !!full.can_manage_onboarding,
        can_view_reports: !!full.can_view_reports,
        can_manage_payroll: !!full.can_manage_payroll,
        can_manage_certifications: !!full.can_manage_certifications,
        can_manage_settings: !!full.can_manage_settings,
        can_manage_announcements: !!full.can_manage_announcements,
        can_manage_expenses: !!full.can_manage_expenses,
        can_manage_performance: !!full.can_manage_performance,
        can_manage_employees: !!full.can_manage_employees,
        can_approve_leave: !!full.can_approve_leave,
        can_approve_expenses: !!full.can_approve_expenses,
        can_start_reviews: !!full.can_start_reviews,
        can_view_team: !!full.can_view_team,
        can_manage_roles: !!full.can_manage_roles,
        can_view_bank_details: !!full.can_view_bank_details,
        can_edit_bank_details: !!full.can_edit_bank_details,
        can_view_bonuses: !!full.can_view_bonuses,
        can_edit_bonuses: !!full.can_edit_bonuses,
        can_manage_deductions: !!full.can_manage_deductions,
      };
      setViewAsLevel(level);
      onClose();
      if (!full.is_admin) navigate("/home");
    } catch {
      setSelectError(
        t("viewAs.selectFailed", "Couldn't load that employee — you may not have visibility of their site."),
      );
    }
  }, [setViewAsLevel, navigate, onClose, t]);

  return (
    <div className="absolute right-0 top-full mt-1 w-72 rounded-lg border border-gray-200 bg-white shadow-lg z-[62]">
      {!viewAsMode && (
        <div className="p-2 space-y-1">
          {viewAsLevel && (
            <button
              type="button"
              onClick={() => { setViewAsLevel(null); onClose(); }}
              className="w-full rounded-md px-3 py-2 text-left text-xs font-medium text-red-600 hover:bg-red-50"
            >
              {t("viewAs.exitPreview")}
            </button>
          )}
          <button
            type="button"
            onClick={() => setViewAsMode("employee")}
            className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-50"
          >
            <span className="font-medium text-gray-900">{t("viewAs.asEmployee")}</span>
            <span className="block text-[11px] text-gray-500">{t("viewAs.asEmployeeDesc")}</span>
          </button>
          {isSuperAdmin && (
            <button
              type="button"
              onClick={() => setViewAsMode("level")}
              className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-50"
            >
              <span className="font-medium text-gray-900">{t("viewAs.asLevel")}</span>
              <span className="block text-[11px] text-gray-500">{t("viewAs.asLevelDesc")}</span>
            </button>
          )}
        </div>
      )}

      {viewAsMode === "employee" && (
        <div className="p-2 space-y-2">
          <button type="button" onClick={() => { setViewAsMode(null); setEmpSearch(""); setSelectError(null); }} className="text-xs text-pme-blue hover:underline">
            &larr; Back
          </button>
          <input
            type="search"
            placeholder={t("viewAs.searchPlaceholder")}
            value={empSearch}
            onChange={(e) => { setEmpSearch(e.target.value); setSelectError(null); }}
            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-pme-blue focus:ring-1 focus:ring-pme-blue"
            autoFocus
          />
          {selectError && <p className="text-xs text-red-600">{selectError}</p>}
          {searchLoading && <p className="text-xs text-gray-400">{t("common.loading")}</p>}
          <ul className="max-h-48 space-y-0.5 overflow-y-auto">
            {empSearch.length < 2 && (
              <li>
                <button
                  type="button"
                  className="w-full rounded-md px-2.5 py-1.5 text-left text-sm hover:bg-gray-50"
                  onClick={() => {
                    setViewAsLevel(standardEmployeeView(t("viewAs.employeeView", "Employee view")));
                    onClose();
                    navigate("/home");
                  }}
                >
                  <span className="font-medium text-gray-900">{t("viewAs.standardEmployee", "Standard employee")}</span>
                  <span className="block text-[11px] text-gray-500">{t("viewAs.standardEmployeeDesc", "A typical employee with no extra permissions")}</span>
                </button>
              </li>
            )}
            {empResults.map((emp) => (
              <li key={emp.id}>
                <button
                  type="button"
                  className="w-full rounded-md px-2.5 py-1.5 text-left text-sm hover:bg-gray-50"
                  onClick={() => selectEmployee(emp)}
                >
                  <span className="font-medium text-gray-900">{emp.first_name} {emp.last_name}</span>
                  {emp.site_name && <span className="ml-1.5 text-[11px] text-gray-400">({emp.site_name})</span>}
                  {emp.access_level_name && <span className="block text-[11px] text-gray-500">{emp.access_level_name}</span>}
                </button>
              </li>
            ))}
            {empSearch.length >= 2 && !searchLoading && empResults.length === 0 && (
              <li className="px-2.5 py-2 text-xs text-gray-400">No employees found</li>
            )}
          </ul>
          <p className="border-t border-gray-100 pt-1.5 text-[10px] leading-snug text-gray-400">
            {t("viewAs.previewHint", "You'll see their menus and permissions. Their personal data is not shown — pages still show your own record.")}
          </p>
        </div>
      )}

      {viewAsMode === "level" && (
        <div className="p-2 space-y-2">
          <button type="button" onClick={() => setViewAsMode(null)} className="text-xs text-pme-blue hover:underline">
            &larr; Back
          </button>
          <ul className="max-h-56 space-y-0.5 overflow-y-auto">
            {accessLevels.map((al) => (
              <li key={al.id}>
                <button
                  type="button"
                  className="w-full rounded-md px-2.5 py-1.5 text-left text-sm hover:bg-gray-50"
                  onClick={() => {
                    const level = {
                      name: al.name,
                      is_admin: !!al.is_admin,
                      rank: al.rank,
                      ...Object.fromEntries(
                        Object.keys(al).filter(k => k.startsWith("can_")).map(k => [k, !!(al as unknown as Record<string, unknown>)[k]])
                      ),
                    } as ViewAsLevel;
                    setViewAsLevel(level);
                    onClose();
                    if (!al.is_admin) navigate("/home");
                  }}
                >
                  <span className="font-medium text-gray-900">{al.name}</span>
                  <span className="ml-1.5 text-[11px] text-gray-400">Rank {al.rank ?? "–"}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared: Lock + View As buttons (inline toolbar)
// ---------------------------------------------------------------------------

export function ViewAsToolbarButtons({ inline }: { inline?: boolean }) {
  const { t } = useTranslation();
  const { viewAsLevel, isSuperAdmin } = useAuth();
  const { adminUnlocked, setAdminUnlocked } = useAdminUnlock();
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [quickPickerOpen, setQuickPickerOpen] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!viewAsOpen && !quickPickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setViewAsOpen(false);
        setQuickPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [viewAsOpen, quickPickerOpen]);

  return (
    <>
      <div ref={containerRef} className={`relative flex items-center gap-1.5 ${inline ? "" : ""}`}>
        {/* Quick employee preview — pick any employee (e.g. Tamlyn) and see
            the app with their menus and permissions */}
        {!viewAsLevel && (
          <button
            type="button"
            onClick={() => { setQuickPickerOpen((v) => !v); setViewAsOpen(false); }}
            title={t("viewAs.asEmployeeQuickHint", "Pick an employee and see the app the way they see it")}
            aria-label={t("viewAs.asEmployeeQuick", "View as employee")}
            className={[
              "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors",
              inline ? "" : "shadow-md backdrop-blur",
              "bg-pme-blue text-white hover:bg-pme-blue/90",
            ].join(" ")}
          >
            <Eye size={13} strokeWidth={2} aria-hidden />
            <span className="hidden sm:inline">
              {t("viewAs.asEmployeeQuick", "View as employee")}
            </span>
          </button>
        )}

        {/* Admin unlock toggle — super admins only */}
        {isSuperAdmin && (
        <button
          type="button"
          onClick={() => {
            if (adminUnlocked) {
              setAdminUnlocked(false);
            } else {
              setPinModalOpen(true);
            }
          }}
          title={adminUnlocked ? t("adminUnlock.relock", "Re-lock") : t("adminUnlock.unlock", "Unlock admin view")}
          className={[
            "flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors",
            inline ? "" : "shadow-md",
            adminUnlocked
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : inline
                ? "bg-gray-100 text-gray-500 hover:bg-gray-200"
                : "bg-white/90 text-gray-500 hover:bg-gray-100 backdrop-blur border border-gray-200",
          ].join(" ")}
        >
          {adminUnlocked ? (
            <LockOpen size={15} strokeWidth={2} aria-hidden />
          ) : (
            <Lock size={15} strokeWidth={2} aria-hidden />
          )}
        </button>
        )}

        {/* View As button — exit / advanced previews */}
        {(isSuperAdmin || viewAsLevel) && (
        <button
          type="button"
          onClick={() => setViewAsOpen((v) => !v)}
          aria-label={viewAsLevel ? undefined : t("viewAs.button")}
          className={[
            "flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[11px] font-medium transition-colors",
            inline ? "" : "shadow-md backdrop-blur",
            viewAsLevel
              ? "bg-amber-500 text-white hover:bg-amber-600"
              : inline
                ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                : "bg-white/90 text-gray-600 hover:bg-gray-100 border border-gray-200",
          ].join(" ")}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
          {viewAsLevel ? viewAsLevel.name : <span className="hidden sm:inline">{t("viewAs.button")}</span>}
        </button>
        )}

        {quickPickerOpen && (
          <ViewAsDropdownPanel initialMode="employee" onClose={() => setQuickPickerOpen(false)} />
        )}
        {viewAsOpen && !quickPickerOpen && <ViewAsDropdownPanel onClose={() => setViewAsOpen(false)} />}
      </div>

      {pinModalOpen && (
        <AdminPinModal
          onSuccess={() => { setAdminUnlocked(true); setPinModalOpen(false); }}
          onClose={() => setPinModalOpen(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main: GlobalViewAs (renders banners + floating toolbar on non-admin pages)
// ---------------------------------------------------------------------------

export default function GlobalViewAs() {
  const { t } = useTranslation();
  const { employee, isKiosk, viewAsLevel, setViewAsLevel } = useAuth();
  const { adminUnlocked } = useAdminUnlock();
  const location = useLocation();

  // The real account's admin status, not the previewed one — the toolbar must
  // stay reachable while previewing so admins can exit.
  const isRealAdmin = !!employee?.is_admin;

  if (!employee || isKiosk || !isRealAdmin) return null;

  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <>
      {/* Preview banner */}
      {viewAsLevel && (
        <div className="sticky top-0 z-[60] bg-amber-500 px-4 py-2 text-white">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-semibold">
            <span>
              {viewAsLevel.access_level_name
                ? `Previewing as ${viewAsLevel.name} (${viewAsLevel.access_level_name}${viewAsLevel.rank != null ? `, Rank ${viewAsLevel.rank}` : ""})`
                : t("viewAs.previewBannerLevel", { level: viewAsLevel.name })}
            </span>
            <button type="button" onClick={() => setViewAsLevel(null)} className="rounded bg-white/20 px-2 py-0.5 text-xs hover:bg-white/30">
              {t("viewAs.exitPreview")}
            </button>
          </div>
          {/* The preview borrows their menus and permissions only — it never
              signs in as them, so personal pages still show the admin's own
              record. Say that plainly, and offer their real record. */}
          <p className="mt-0.5 text-center text-xs font-medium text-amber-50">
            {viewAsLevel.employee_id
              ? t("viewAs.bannerOwnData", {
                  defaultValue: "Menus and permissions only — personal details on screen are still yours ({{me}}), not {{name}}'s.",
                  me: employee.name,
                  name: viewAsLevel.name,
                })
              : t("viewAs.bannerOwnDataLevel", {
                  defaultValue: "Menus and permissions only — personal details on screen are still yours.",
                })}
            {viewAsLevel.employee_id && (
              <>
                {" "}
                <Link
                  to={`/admin/employees/${viewAsLevel.employee_id}`}
                  onClick={() => setViewAsLevel(null)}
                  className="underline underline-offset-2 hover:text-white"
                >
                  {t("viewAs.openRealRecord", { defaultValue: "Open {{name}}'s record", name: viewAsLevel.name })}
                </Link>
              </>
            )}
          </p>
        </div>
      )}

      {/* Admin unlock banner */}
      {adminUnlocked && !viewAsLevel && (
        <div className="sticky top-0 z-[59] flex items-center justify-center gap-2 bg-blue-600 px-4 py-1.5 text-xs font-medium text-white">
          {t("adminUnlock.viewUnlocked", "Admin view unlocked")}
        </div>
      )}

      {/* Floating toolbar — only on non-admin pages */}
      {!isAdminRoute && (
        <div className="fixed right-3 z-[61]" style={{ top: "calc(env(safe-area-inset-top, 0px) + 0.5rem)" }}>
          <ViewAsToolbarButtons />
        </div>
      )}
    </>
  );
}
